import type { C1_Furniture } from "./C1_Furniture";
import type { C1_Room } from "./C1_Room";
import type { DropPreview } from "../types/C1_AppTypes";
import type { C1_FurnitureData } from "../types/C1_ModelTypes";
import type { C1_LegacyEloItemTreeData } from "../types/C1_EloTypes";
import { cloneEloTreeWithFreshIds } from "./C1_cloneEloTree";

export type C1_FurnitureClipboard = {
  roomId: string;
  /** Serialized native device graph, including channel settings and owned children. */
  native?: { data: string; className: string; name: string };
  data: C1_FurnitureData;
  eloTree?: C1_LegacyEloItemTreeData;
};

export function copyFurniture(furniture: C1_Furniture): C1_FurnitureClipboard {
  const room = furniture.room;
  if (!room) throw new Error("Cannot copy furniture without a room.");
  return structuredClone({
    roomId: room.id,
    data: { ...furniture.data, baseProps: furniture.baseProps, masterIndex: undefined },
    eloTree: furniture.eloTree,
  });
}

/** Rebuilds independent equipment through the same materialization path as the furniture editor. */
export function pasteFurniture(
  clipboard: C1_FurnitureClipboard,
  room: C1_Room,
  placement: Extract<DropPreview, { type: "furniture" }>,
): C1_Furniture {
  const { data, eloTree } = structuredClone(clipboard);
  const rect = room.innerRect();
  const width = rect.x2 - rect.x1;
  const height = rect.y2 - rect.y1;
  if (width <= 0 || height <= 0) throw new Error("Cannot paste furniture into a room without usable dimensions.");
  const furniture = room.addFurniture({
    ...data,
    side: placement.side,
    _wallId: placement.wallId,
    wallDocked: placement.wallDocked,
    reflect: placement.reflect ?? data.reflect,
    relativePosition: {
      x: (placement.x - rect.x1) / width,
      y: (placement.y - rect.y1) / height,
    },
    baseProps: { ...data.baseProps, rotation: placement.rotation },
  });
  if (eloTree) furniture.setEloTree(cloneEloTreeWithFreshIds(eloTree));
  return furniture;
}
